import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, Pencil, Trash2, CalendarRange, Wrench, Activity, PiggyBank, Users, ArrowLeft, CalendarPlus, RefreshCw } from 'lucide-react';
import QuarterSetup from './QuarterSetup';
import SaBudgetChecks from './SaBudgetChecks';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Panel, Register, DetailDrawer, AcStatus, Currency, TableSkeleton, ErrorState,
  Field, FormRow, fmtDate, money, CATEGORY_HUE,
} from './design';

const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const today = () => isoDate();
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
// A saved record into the form: a date input wants YYYY-MM-DD, and a blank number stays blank
// rather than becoming 0.
const day = (v) => (v ? String(v).slice(0, 10) : '');
const numText = (v) => (v == null ? '' : String(v));

// A status kept on an older record that the list no longer offers still shows as itself, so
// opening the record to edit something else does not quietly change it.
function StatusSelect({ id, value, onChange, options }) {
  const known = options.some(([v]) => v === value);
  return (
    <select id={id} value={value} onChange={onChange} className={INP}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      {!known && value && <option value={value}>{cap(value)}</option>}
    </select>
  );
}
const ATHM_STATUS = [['active', 'Active'], ['pending', 'Pending'], ['closed', 'Closed']];
// 'Ended' and 'Cancelled' are the words the counts and the claim checks already read.
const PATHWAY_STATUS = [['active', 'Active'], ['ended', 'Ended'], ['cancelled', 'Cancelled']];

// The number of days an entry has, and whether it is inside its budget, decide the row edge:
// overspent is rose, burning faster than the calendar is amber.
const quarterTone = (b) => (Number(b.remaining) < 0 ? 'rose' : b.atRisk ? 'amber' : undefined);

// Every participant's budget this quarter, before one is chosen. The same states as the overview:
// over budget, on course to run out before the quarter ends, and on course to finish with more left
// than may be carried over (the higher of $1,000 or 10% of the quarterly budget).
const HEALTH = {
  over: { label: 'Over budget', status: 'overdue', bar: 'bg-red-500' },
  fast: { label: 'Will run out', status: 'expiring', bar: 'bg-amber-500' },
  slow: { label: 'Underused', status: 'pending', bar: 'hue-bar opacity-50' },
  on_track: { label: 'On track', status: 'current', bar: 'hue-bar' },
};
const healthTone = (b) => (b.state === 'over' ? 'rose' : b.state === 'fast' || b.state === 'slow' ? 'amber' : undefined);
function UsedBar({ spent, pool, pace, state }) {
  const fill = Math.min(1, pool > 0 ? spent / pool : 0);
  return (
    <div className="flex items-center gap-2.5 min-w-[9rem]">
      <div className="relative flex-1 h-2 rounded-full bg-slate-100">
        <div className="absolute inset-0 overflow-hidden rounded-full">
          <div className={clsx('h-full rounded-full', (HEALTH[state] || HEALTH.on_track).bar)} style={{ width: `${fill * 100}%` }} />
        </div>
        <span className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded bg-slate-700/70" style={{ left: `calc(${pace * 100}% - 1px)` }} aria-hidden="true" />
      </div>
      <span className="w-10 text-right tabular-nums text-[12px] font-medium text-slate-700">{Math.round((pool > 0 ? spent / pool : 0) * 100)}%</span>
    </div>
  );
}
const catHead = (k) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={clsx('w-2 h-2 rounded-full', CATEGORY_HUE[k].bar)} aria-hidden="true" />{CATEGORY_HUE[k].label}
  </span>
);

export default function AgedCareSahBudgets({ clientId: fixedClient = '', embedded = false } = {}) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(fixedClient || '');
  useEffect(() => { if (fixedClient) setClientId(fixedClient); }, [fixedClient]);
  const [budgets, setBudgets] = useState([]);
  const [athm, setAthm] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(false);
  // One message per list: a list that could not load says so instead of looking like a
  // participant with none of that kind.
  const [loadErrors, setLoadErrors] = useState({});
  const [modal, setModal] = useState(null); // {kind, id when editing, ...}
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const [clientsError, setClientsError] = useState(null);
  const loadClients = useCallback(() => {
    setClientsError(null);
    api.get('/aged-care/clients').then((r) => setClients(r.data || []))
      .catch((e) => setClientsError(e.response?.data?.message || 'Could not load the participants'));
  }, []);
  useEffect(() => { loadClients(); }, [loadClients]);
  // The provider's pooled care management account: 10% of every participant's quarterly budget.
  const [cmAccount, setCmAccount] = useState(null);
  useEffect(() => { api.get('/aged-care/care-management/account').then((r) => setCmAccount(r.data)).catch(() => setCmAccount(null)); }, []);
  // This quarter, every participant: what the screen shows until one is chosen.
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const loadSummary = useCallback(() => {
    setSummaryError(null);
    api.get('/aged-care/budgets/summary').then((r) => setSummary(r.data))
      .catch((e) => { setSummary(null); setSummaryError(e.response?.data?.message || "Could not load this quarter's budgets"); });
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  // Every participant's budget for a quarter at once, and a re-check of every booked visit.
  const [setupOpen, setSetupOpen] = useState(false);
  const closeSetup = useCallback(() => setSetupOpen(false), []);
  const [checking, setChecking] = useState(false);
  const recheck = async () => {
    setChecking(true);
    try {
      const r = await api.post('/aged-care/bookings/recheck');
      const v = r.data || {};
      toast.success(`${v.checked} upcoming visit${v.checked === 1 ? '' : 's'} checked. ${v.held} held for funding${v.released ? `, ${v.released} back live` : ''}.`);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not check the bookings'); }
    finally { setChecking(false); }
  };

  const load = useCallback(async () => {
    if (!clientId) { setBudgets([]); setAthm([]); setPathways([]); setLoadErrors({}); return; }
    setLoading(true);
    const failed = (what) => (e) => ({ error: e.response?.data?.message || `Could not load the ${what}` });
    try {
      const [b, a, p] = await Promise.all([
        api.get('/aged-care/budgets', { params: { client_id: clientId } }).catch(failed('quarterly budgets')),
        api.get('/aged-care/athm', { params: { client_id: clientId } }).catch(failed('AT-HM entitlements')),
        api.get('/aged-care/pathway-budgets', { params: { client_id: clientId } }).catch(failed('pathway budgets')),
      ]);
      setBudgets(b.data || []); setAthm(a.data || []); setPathways(p.data || []);
      setLoadErrors({ budgets: b.error || null, athm: a.error || null, pathways: p.error || null });
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  // A changed quarter also changes the every-participant list, which is loaded once and would
  // otherwise show the old figure when "All participants" is chosen again.
  const reload = () => { load(); loadSummary(); };

  const openQuarter = () => { setF({ quarter_start: today(), quarter_end: '', amount: '', carryover_cap: '', notes: '' }); setModal({ kind: 'quarter' }); };
  const openAthm = () => { setF({ tier: '', amount: '', spent: '', status: 'active', approved_date: today(), notes: '' }); setModal({ kind: 'athm' }); };
  const openPathway = () => { setF({ type: 'restorative', amount: '', spent: '', start_date: today(), end_date: '', status: 'active', notes: '' }); setModal({ kind: 'pathway' }); };

  const editQuarter = (b) => {
    setF({ quarter_start: day(b.quarter_start), quarter_end: day(b.quarter_end), amount: numText(b.amount), carryover_in: numText(b.carryover_in),
      carryover_cap: numText(b.carryover_cap), notes: b.notes || '' });
    setModal({ kind: 'quarter', id: b.id });
  };
  const editAthm = (x) => {
    setF({ tier: x.tier || '', amount: numText(x.amount), spent: numText(x.spent), status: x.status || 'active', approved_date: day(x.approved_date),
      expires_on: day(x.expires_on), notes: x.notes || '' });
    setModal({ kind: 'athm', id: x.id });
  };
  // `spent` here is the figure kept by hand, not the list's, which also counts claimed visits.
  const editPathway = (x) => {
    setF({ type: x.type || 'restorative', amount: numText(x.amount), spent: numText(x.spent), start_date: day(x.start_date), end_date: day(x.end_date),
      status: x.status || 'active', notes: x.notes || '' });
    setModal({ kind: 'pathway', id: x.id, amount: numText(x.amount) });
  };

  const openSteps = (x) => {
    setF({ id: x.id, type: x.type, start_date: day(x.start_date), end_date: day(x.end_date), referral_accepted_at: day(x.referral_accepted_at), entry_notified_at: day(x.entry_notified_at),
      review_requested_at: day(x.review_requested_at), review_approved_at: day(x.review_approved_at), outcome: x.outcome || '', start_notification_at: day(x.start_notification_at),
      week12: x.week12, week16: x.week16 });
    setModal({ kind: 'steps' });
  };
  const submit = async () => {
    setSaving(true);
    try {
      if (modal.kind === 'steps') {
        const { id, week12, week16, type, ...body } = f;
        await api.put(`/aged-care/pathway-budgets/${id}`, body);
        toast.success('Saved'); setModal(null); load(); setSaving(false); return;
      }
      if (modal.kind === 'quarter') {
        if (!f.quarter_start || !f.quarter_end) { toast.error('Quarter start and end are required'); setSaving(false); return; }
        const body = { ...f, amount: Number(f.amount) || 0, carryover_cap: f.carryover_cap === '' ? null : Number(f.carryover_cap) };
        // Carryover is worked out only when a quarter is added; after that it is what is entered.
        if (modal.id) await api.put(`/aged-care/budgets/${modal.id}`, { ...body, carryover_in: Number(f.carryover_in) || 0 });
        else await api.post('/aged-care/budgets', { client_id: clientId, ...body });
      } else if (modal.kind === 'athm') {
        const body = { ...f, amount: Number(f.amount) || 0, spent: Number(f.spent) || 0 };
        if (modal.id) await api.put(`/aged-care/athm/${modal.id}`, body);
        else await api.post('/aged-care/athm', { client_id: clientId, ...body });
      } else if (modal.id) {
        // Only the budget's own fields: the steps are saved from their own form and stay as they are.
        const body = { type: f.type, amount: Number(f.amount) || 0, spent: Number(f.spent) || 0, start_date: f.start_date, end_date: f.end_date, status: f.status, notes: f.notes };
        // An amount changed by hand is no longer the government's or Services Australia's figure.
        if (f.amount !== modal.amount) body.amount_source = null;
        await api.put(`/aged-care/pathway-budgets/${modal.id}`, body);
      } else {
        await api.post('/aged-care/pathway-budgets', { client_id: clientId, ...f, amount: f.amount === '' ? null : Number(f.amount) || 0, spent: Number(f.spent) || 0 });
      }
      toast.success('Saved'); setModal(null); reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  const del = async (kind, id) => {
    const path = kind === 'quarter' ? 'budgets' : kind === 'athm' ? 'athm' : 'pathway-budgets';
    const what = kind === 'quarter' ? 'quarterly budget' : kind === 'athm' ? 'AT-HM entitlement' : 'pathway budget';
    const ok = await confirm(`The ${what} is removed from this participant's budgets.`, { title: `Delete this ${what}?`, confirmLabel: `Delete ${what}`, danger: true });
    if (!ok) return;
    try {
      await api.delete(`/aged-care/${path}/${id}`);
      toast.success(`${cap(what)} deleted`); reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete'); }
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // Stable, or the drawer re-runs its focus effect on every keystroke inside the form.
  const closeModal = useCallback(() => setModal(null), []);

  const editButton = (onClick, what) => (
    <button type="button" onClick={onClick} title={`Edit ${what}`} aria-label={`Edit ${what}`}
      className="btn-ghost btn-sm !px-1.5"><Pencil size={14} /></button>
  );
  const deleteButton = (kind, id, what) => (
    <button type="button" onClick={() => del(kind, id)} title={`Delete ${what}`} aria-label={`Delete ${what}`}
      className="btn-ghost btn-sm !px-1.5 hover:!text-red-700"><Trash2 size={14} /></button>
  );

  const quarterCols = [
    {
      key: 'quarter', header: 'Quarter', sortable: true, sortKey: 'quarter_start',
      render: (b) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900">{fmtDate(b.quarter_start)} to {fmtDate(b.quarter_end)}</span>
            {Number(b.remaining) < 0 ? <AcStatus status="overdue" label="Overspent" /> : b.atRisk ? <AcStatus status="expiring" label="At risk" /> : null}
          </div>
          <div className="text-[11px] text-slate-500">{b.daysLeft > 0 ? `${b.daysLeft} days left` : 'Ended'}</div>
        </div>
      ),
    },
    {
      key: 'pool', header: 'For services', align: 'right', sortable: true, sortValue: (b) => Number(b.pool) || 0,
      render: (b) => (
        <div>
          <Currency value={b.pool} className="font-medium" />
          {Number(b.careMgmtAllocation) > 0 && <div className="text-[11px] text-slate-500">after {money(b.careMgmtAllocation)} (10%) to care management</div>}
          {Number(b.carryover_in) > 0 && <div className="text-[11px] text-slate-500">includes {money(b.carryover_in)} carried over</div>}
        </div>
      ),
    },
    { key: 'spent', header: 'Spent', align: 'right', sortable: true, sortValue: (b) => Number(b.spent) || 0, render: (b) => <Currency value={b.spent} /> },
    {
      // What Services Australia's payment statements say came off the ongoing budget for services in
      // the quarter, including anything claimed outside Rostery. Only up to the last statement.
      key: 'saDrawn', header: 'Services Australia drew', align: 'right', sortable: true, sortValue: (b) => (b.saDrawn == null ? -1 : Number(b.saDrawn)),
      render: (b) => (b.saDrawn == null
        ? <span className="text-[12px] text-slate-400">No statement yet</span>
        : (
          <div>
            <Currency value={b.saDrawn} />
            <div className="text-[11px] text-slate-500">services to {fmtDate(b.saConfirmedTo)}</div>
          </div>
        )),
    },
    { key: 'remaining', header: 'Remaining', align: 'right', sortable: true, sortValue: (b) => Number(b.remaining) || 0, render: (b) => <Currency value={b.remaining} className="font-medium" /> },
    {
      key: 'burn', header: 'Burn', align: 'right', sortable: true, sortValue: (b) => Number(b.burnPct) || 0,
      render: (b) => (
        <div>
          <div className={clsx('tabular-nums font-medium', b.atRisk ? 'text-amber-700' : 'text-slate-700')}>{b.burnPct}%</div>
          <div className="text-[11px] text-slate-500 tabular-nums">expected {b.expectedPct}%</div>
        </div>
      ),
    },
    { key: 'clinical', header: catHead('clinical'), align: 'right', render: (b) => <Currency value={b.spentByCategory?.clinical || 0} className="text-slate-500" /> },
    { key: 'independence', header: catHead('independence'), align: 'right', render: (b) => <Currency value={b.spentByCategory?.independence || 0} className="text-slate-500" /> },
    { key: 'everyday', header: catHead('everyday'), align: 'right', render: (b) => <Currency value={b.spentByCategory?.everyday || 0} className="text-slate-500" /> },
  ];

  const q = summary?.quarter;
  const pace = q && q.daysTotal ? q.daysElapsed / q.daysTotal : 0;
  const summaryCols = [
    {
      key: 'name', header: 'Participant', sortable: true,
      render: (b) => (
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900">{b.name}</span>
          <AcStatus status={(HEALTH[b.state] || HEALTH.on_track).status} label={(HEALTH[b.state] || HEALTH.on_track).label} />
        </div>
      ),
    },
    { key: 'pool', header: 'For services', align: 'right', sortable: true, sortValue: (b) => b.pool, render: (b) => <Currency value={b.pool} /> },
    { key: 'spent', header: 'Spent', align: 'right', sortable: true, sortValue: (b) => b.spent, render: (b) => <Currency value={b.spent} /> },
    {
      key: 'remaining', header: 'Remaining', align: 'right', sortable: true, sortValue: (b) => b.remaining,
      render: (b) => (
        <div>
          <Currency value={b.remaining} className={clsx('font-medium', b.remaining < 0 && 'text-red-700')} />
          {b.state === 'slow' && b.forfeit > 0 && <div className="text-[11px] text-amber-700">{money(b.forfeit)} over the carryover limit</div>}
        </div>
      ),
    },
    { key: 'used', header: 'Used, against the quarter', width: 220, sortable: true, sortValue: (b) => b.usedPct, render: (b) => <UsedBar spent={b.spent} pool={b.pool} pace={pace} state={b.state} /> },
  ];

  const athmCols = [
    {
      key: 'tier', header: 'Entitlement',
      render: (x) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{x.tier || 'AT-HM'}</div>
          {x.approved_date && <div className="text-[11px] text-slate-500">Approved {fmtDate(x.approved_date)}{x.expires_on ? `, ends ${fmtDate(x.expires_on)}` : ''}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', width: 96, render: (x) => <AcStatus status={x.status} /> },
    { key: 'amount', header: 'Amount', align: 'right', render: (x) => <Currency value={x.amount} /> },
    { key: 'spent', header: 'Spent', align: 'right', render: (x) => <Currency value={x.spent} /> },
    { key: 'remaining', header: 'Remaining', align: 'right', render: (x) => <Currency value={x.remaining} className="font-medium" /> },
  ];

  const pathwayCols = [
    {
      key: 'type', header: 'Pathway',
      render: (x) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{cap(x.type)}</div>
          {x.start_date && <div className="text-[11px] text-slate-500">{fmtDate(x.start_date)} to {fmtDate(x.end_date || x.last)}</div>}
          {x.type === 'end_of_life' && x.week12 && <div className="text-[11px] text-slate-500">Week 12 {fmtDate(x.week12)} · at most to {fmtDate(x.week16)}</div>}
          {x.type === 'end_of_life' && !x.entry_notified_at && <div className="text-[11px] font-medium text-red-700">Entry not notified yet: its visits are held</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', width: 96, render: (x) => <AcStatus status={x.status} /> },
    { key: 'amount', header: 'Amount', align: 'right', render: (x) => <Currency value={x.amount} /> },
    // Visits claimed from the pathway's own account in the episode (or the amount entered, if more).
    { key: 'spent', header: 'Spent', align: 'right', render: (x) => <Currency value={x.spent_live ?? x.spent} /> },
    { key: 'remaining', header: 'Remaining', align: 'right', render: (x) => <Currency value={x.remaining} className="font-medium" /> },
  ];

  const kind = modal ? modal.kind : null;
  const editing = !!(modal && modal.id);
  const formTitle = kind === 'steps' ? 'Pathway steps'
    : `${editing ? 'Edit' : 'Add'} ${kind === 'quarter' ? 'quarterly budget' : kind === 'athm' ? 'AT-HM entitlement' : 'pathway budget'}`;
  const saveLabel = kind === 'steps' ? 'Save steps' : editing ? 'Save changes' : kind === 'quarter' ? 'Save quarter' : kind === 'athm' ? 'Save entitlement' : 'Save pathway';
  const notesField = (
    <Field label="Notes">{(id) => <textarea id={id} rows={2} value={f.notes || ''} onChange={set('notes')} className={INP} />}</Field>
  );

  return (
    <div>
      {!embedded && <>
      <PageHead icon={CalendarRange}
        title="Support at Home budgets"
        purpose="A participant's quarterly pool and its carryover, their assistive technology and home modifications entitlement, which sits outside the pool, and any short-term restorative or end-of-life pathway budget."
        actions={
          <>
            {clientId && (
              <button type="button" onClick={() => setClientId('')} className="btn-ghost btn-sm">
                <ArrowLeft size={14} aria-hidden="true" /> All participants
              </button>
            )}
            <button type="button" onClick={() => setSetupOpen(true)} className="btn-primary">
              <CalendarPlus size={15} aria-hidden="true" /> Set up a quarter
            </button>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Participant"
              className={clsx(INP, '!w-auto min-w-[240px]')}>
              <option value="">All participants</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        }
      />

      {cmAccount && (
        <Panel title="Care management account" icon={PiggyBank} className="mb-5" bodyClass="px-4 py-3">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div>
              <div className="text-[11px] text-slate-500">Balance</div>
              <div className={clsx('text-lg font-semibold tabular-nums', cmAccount.balance < 0 ? 'text-red-700' : 'text-slate-900')}>{money(cmAccount.balance)}</div>
            </div>
            <p className="text-[12px] text-slate-500 max-w-xl">
              10% of every participant's quarterly budget, pooled across all of them. Care management you log with time and a rate is claimed from here,
              not from the participant's budget. Unspent money rolls over each quarter.
            </p>
          </div>
          {cmAccount.quarters?.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3 font-medium">Quarter from</th><th className="py-1 pr-3 font-medium text-right">Credited</th><th className="py-1 pr-3 font-medium text-right">Claimed</th><th className="py-1 font-medium text-right">Closing</th></tr></thead>
                <tbody>
                  {cmAccount.quarters.slice(0, 6).map((q) => (
                    <tr key={q.quarter} className="border-t border-slate-100">
                      <td className="py-1 pr-3">{fmtDate(q.quarter)}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{money(q.credited)}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{money(q.spent)}</td>
                      <td className={clsx('py-1 text-right tabular-nums', q.overdrawn && 'text-red-700 font-medium')}>{money(q.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
      </>}

      {clientsError && <Panel className="mb-5"><ErrorState message={clientsError} onRetry={loadClients} /></Panel>}

      {!clientId ? (
        <Register
          title="This quarter, every participant" icon={Users}
          count={summary ? summary.participants : undefined} countLabel={summary && summary.participants === 1 ? 'participant' : 'participants'}
          columns={summaryCols} rows={summary ? summary.list : []} rowKey="clientId" rowTone={healthTone}
          loading={!summary && !summaryError} error={summaryError} onRetry={loadSummary}
          onRowClick={(b) => setClientId(b.clientId)}
          actions={<>
            {q ? <span className="text-[12px] text-slate-500">{fmtDate(q.start)} to {fmtDate(q.end)}, {q.daysLeft > 0 ? `${q.daysLeft} days left` : 'last day'}</span> : null}
            <button type="button" onClick={recheck} disabled={checking} className="btn-secondary btn-sm" title="Check every booked visit against plans and budgets">
              <RefreshCw size={13} className={checking ? 'animate-spin motion-reduce:animate-none' : ''} aria-hidden="true" /> {checking ? 'Checking…' : 'Check bookings'}
            </button>
          </>}
          empty={{
            icon: CalendarRange,
            title: 'No quarterly budgets for this quarter',
            hint: 'Choose a participant above and add the quarter from their Notice of Decision. Their spending is then tracked here against the calendar.',
          }}
          footer={summary && (summary.participants > 0 || summary.withoutBudget > 0) ? (
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-0.5 h-3 rounded bg-slate-700/70" aria-hidden="true" />Where spending should be today</span>
              {summary.withoutBudget > 0 && (
                <span className="text-amber-800">{summary.withoutBudget} participant{summary.withoutBudget === 1 ? ' has' : 's have'} no budget entered for this quarter.</span>
              )}
              <span>Choose a participant to see their quarters, AT-HM and pathways.</span>
            </span>
          ) : null}
        />
      ) : loading ? (
        <Panel><TableSkeleton rows={4} cols={5} /></Panel>
      ) : (
        <div className="space-y-5">
          <Register
            title="Quarterly budgets" count={loadErrors.budgets ? undefined : budgets.length} countLabel={budgets.length === 1 ? 'quarter' : 'quarters'}
            columns={quarterCols} rows={budgets} rowTone={quarterTone}
            error={loadErrors.budgets} onRetry={load}
            rowActions={(b) => (
              <span className="inline-flex items-center gap-1">
                {editButton(() => editQuarter(b), 'quarter')}
                {deleteButton('quarter', b.id, 'quarter')}
              </span>
            )}
            actions={<button type="button" onClick={openQuarter} className="btn-primary btn-sm"><Plus size={14} /> Add quarter</button>}
            empty={{
              icon: CalendarRange,
              title: 'No quarterly budgets yet',
              hint: 'Add the current quarter to start tracking spend against the classification pool. Carryover from the prior quarter is worked out when you save.',
              action: <button type="button" onClick={openQuarter} className="btn-primary btn-sm"><Plus size={14} /> Add quarter</button>,
            }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Register
              title="Assistive technology and home modifications" count={loadErrors.athm ? undefined : athm.length} dense
              columns={athmCols} rows={athm}
              error={loadErrors.athm} onRetry={load}
              rowActions={(x) => (
                <span className="inline-flex items-center gap-1">
                  {editButton(() => editAthm(x), 'entitlement')}
                  {deleteButton('athm', x.id, 'entitlement')}
                </span>
              )}
              actions={<button type="button" onClick={openAthm} className="btn-secondary btn-sm"><Plus size={14} /> Add entitlement</button>}
              empty={{
                icon: Wrench,
                title: 'No AT-HM entitlement recorded',
                hint: 'Record the approved amount so spend on equipment and home modifications is tracked separately from the quarterly pool.',
                action: <button type="button" onClick={openAthm} className="btn-secondary btn-sm"><Plus size={14} /> Add entitlement</button>,
              }}
            />
            <Register
              title="Pathway budgets" count={loadErrors.pathways ? undefined : pathways.length} dense
              columns={pathwayCols} rows={pathways}
              error={loadErrors.pathways} onRetry={load}
              rowActions={(x) => (
                <span className="inline-flex items-center gap-1">
                  <button type="button" onClick={() => openSteps(x)} className="btn-ghost btn-sm">Steps</button>
                  {editButton(() => editPathway(x), 'pathway budget')}
                  {deleteButton('pathway', x.id, 'pathway budget')}
                </span>
              )}
              actions={<button type="button" onClick={openPathway} className="btn-secondary btn-sm"><Plus size={14} /> Add pathway</button>}
              empty={{
                icon: Activity,
                title: 'No pathway budgets',
                hint: 'Add a restorative care or end-of-life pathway when one is approved, with its start and end dates.',
                action: <button type="button" onClick={openPathway} className="btn-secondary btn-sm"><Plus size={14} /> Add pathway</button>,
              }}
            />
          </div>
        </div>
      )}

      <SaBudgetChecks clients={clients} clientId={clientId} />
      <QuarterSetup open={setupOpen} onClose={closeSetup} onDone={() => { loadSummary(); load(); }} />
      <DetailDrawer
        open={!!modal} onClose={closeModal} width="md" title={formTitle}
        icon={kind === 'quarter' ? CalendarRange : kind === 'athm' ? Wrench : Activity}
        footer={<>
          <button type="button" onClick={closeModal} className="btn-secondary text-sm">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="btn-primary text-sm">{saving ? 'Saving…' : saveLabel}</button>
        </>}
      >
        {kind === 'quarter' && (
          <div className="space-y-4">
            <FormRow cols={2}>
              <Field label="Quarter start" required>{(id) => <input id={id} type="date" value={f.quarter_start} onChange={set('quarter_start')} className={INP} />}</Field>
              <Field label="Quarter end" required>{(id) => <input id={id} type="date" value={f.quarter_end} onChange={set('quarter_end')} className={INP} />}</Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="Budget amount ($)">{(id) => <input id={id} type="number" step="0.01" value={f.amount} onChange={set('amount')} className={INP} placeholder="0.00" />}</Field>
              <Field label="Carryover cap ($)" hint="The most unspent money that can roll in from the prior quarter.">{(id) => <input id={id} type="number" step="0.01" value={f.carryover_cap} onChange={set('carryover_cap')} className={INP} placeholder="Optional" />}</Field>
            </FormRow>
            {editing && (
              <Field label="Carryover in ($)" hint="Unspent money brought in from the prior quarter.">{(id) => <input id={id} type="number" step="0.01" value={f.carryover_in} onChange={set('carryover_in')} className={INP} placeholder="0.00" />}</Field>
            )}
            {notesField}
            <p className="text-[12px] text-slate-500">
              {editing
                ? 'Carryover is not worked out again when you save changes. If it is wrong, correct it here.'
                : 'Carryover from the prior quarter is worked out, and capped, when you save.'}
            </p>
          </div>
        )}
        {kind === 'athm' && (
          <div className="space-y-4">
            <Field label="Tier or description">{(id) => <input id={id} value={f.tier} onChange={set('tier')} className={INP} placeholder="AT tier 2, wheelchair, for example" />}</Field>
            <FormRow cols={2}>
              <Field label="Amount ($)">{(id) => <input id={id} type="number" step="0.01" value={f.amount} onChange={set('amount')} className={INP} />}</Field>
              <Field label="Spent ($)">{(id) => <input id={id} type="number" step="0.01" value={f.spent} onChange={set('spent')} className={INP} />}</Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="Status">{(id) => <StatusSelect id={id} value={f.status} onChange={set('status')} options={ATHM_STATUS} />}</Field>
              <Field label="Approved date">{(id) => <input id={id} type="date" value={f.approved_date} onChange={set('approved_date')} className={INP} />}</Field>
            </FormRow>
            <Field label="Funding ends" hint="AT-HM funding cannot be paused or carried past this date. You are reminded 60 days before.">{(id) => <input id={id} type="date" value={f.expires_on || ''} onChange={set('expires_on')} className={INP} />}</Field>
            {notesField}
          </div>
        )}
        {kind === 'pathway' && (
          <div className="space-y-4">
            <Field label="Type">
              {(id) => (
                <select id={id} value={f.type} onChange={set('type')} className={INP}>
                  <option value="restorative">Restorative care pathway</option><option value="end_of_life">End-of-life pathway</option>
                </select>
              )}
            </Field>
            <FormRow cols={2}>
              {/* The government figure fills a blank amount only when a pathway is added. */}
              <Field label="Amount ($)" hint={editing ? undefined : 'Leave empty to use the government figure for the start date.'}>{(id) => <input id={id} type="number" step="0.01" value={f.amount} onChange={set('amount')} className={INP} placeholder={editing ? '0.00' : 'Government figure'} />}</Field>
              <Field label="Spent ($)" hint={editing ? 'Entered by hand. If the visits claimed from this pathway come to more, the list shows that instead.' : undefined}>{(id) => <input id={id} type="number" step="0.01" value={f.spent} onChange={set('spent')} className={INP} />}</Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="Start" hint={f.type === 'end_of_life' ? 'The start date on the Aged Care Entry Record.' : undefined}>{(id) => <input id={id} type="date" value={f.start_date} onChange={set('start_date')} className={INP} />}</Field>
              <Field label="End" hint={f.type === 'end_of_life' ? 'Leave empty: it runs up to 112 days, or until a review is approved.' : undefined}>{(id) => <input id={id} type="date" value={f.end_date} onChange={set('end_date')} className={INP} />}</Field>
            </FormRow>
            {editing && <Field label="Status">{(id) => <StatusSelect id={id} value={f.status} onChange={set('status')} options={PATHWAY_STATUS} />}</Field>}
            {notesField}
            {f.type === 'end_of_life' && <p className="text-[12px] text-slate-500">End-of-Life is used once only and replaces the ongoing classification while it runs. Its visits are held until entry is notified: record that under Steps.</p>}
          </div>
        )}
        {kind === 'steps' && (
          <div className="space-y-4">
            {f.type === 'end_of_life' && f.week12 && <p className="text-[12.5px] text-slate-600">Week 12 is {fmtDate(f.week12)}; it can run to {fmtDate(f.week16)} while money remains.</p>}
            <FormRow cols={2}>
              <Field label="Referral accepted in the My Aged Care portal">{(id) => <input id={id} type="date" value={f.referral_accepted_at} onChange={set('referral_accepted_at')} className={INP} />}</Field>
              <Field label="Entry notified in the Provider Portal">{(id) => <input id={id} type="date" value={f.entry_notified_at} onChange={set('entry_notified_at')} className={INP} />}</Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="Support Plan Review asked for" hint={'Reason: "Review End-of-Life Pathway", ideally in week 12 or 13.'}>{(id) => <input id={id} type="date" value={f.review_requested_at} onChange={set('review_requested_at')} className={INP} />}</Field>
              <Field label="Review approved" hint="Approval ends the pathway.">{(id) => <input id={id} type="date" value={f.review_approved_at} onChange={set('review_approved_at')} className={INP} />}</Field>
            </FormRow>
            <FormRow cols={2}>
              <Field label="What happens next">
                {(id) => (
                  <select id={id} value={f.outcome} onChange={set('outcome')} className={INP}>
                    <option value="">Not decided yet</option>
                    <option value="reinstated">Back to their previous ongoing classification</option>
                    <option value="new_classification">New participant: the classification from their assessment</option>
                    <option value="residential_care">Moved to residential care</option>
                    <option value="died">Died</option>
                  </select>
                )}
              </Field>
              {f.outcome === 'new_classification' && (
                <Field label="Start notification sent" hint="Ongoing visits are held until it is.">{(id) => <input id={id} type="date" value={f.start_notification_at} onChange={set('start_notification_at')} className={INP} />}</Field>
              )}
            </FormRow>
            {(f.outcome === 'died' || f.outcome === 'residential_care') && (
              <Field label="Last day of services">{(id) => <input id={id} type="date" value={f.end_date} onChange={set('end_date')} className={INP} />}</Field>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}
